/* eslint-disable @typescript-eslint/no-shadow */
import './ServiceRequestDetails.scss';

import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import moment from 'moment';
import { useParams } from 'react-router-dom';

import Header from '../../../../components/Header/Header';
import Loader from '../../../../components/V2/Loader/Loader';
import {
  useTQConnectServiceRequestById,
  useTQConnectServiceRequestResponses,
} from '../../../../hooks/queries/tqConnectQueries/service-requests';
import showAppError from '../../../../shared/error';
import {
  useAppNavigate,
  useAppSelector,
} from '../../../../shared/hooks';
import { checkTQConnectAccess } from '../../../../shared/tqConnectUtils';
import { checkIfCurrentLoggedInUser } from '../../../../shared/utils';
import { ROUTES } from '../../../../types/routes';
import {
  ITQServiceRequestResponsesResult,
  TQServiceRequestStatusType,
  TQServiceRequestTypeValue,
} from '../../../../types/tqConnectTypes/service-requests';
import ServiceRequestActions
  from '../../components/ServiceRequestActions/ServiceRequestActions';
import ServiceRequestMessageRoom
  from '../../components/ServiceRequestMessageRoom/ServiceRequestMessageRoom';
import TQConnectAccessWrapper from '../../components/TQConnectAccessWrapper';
import ServiceRequestStatusTag
  from '../../components/TQServiceRequestResponseBottpmSheet/ServiceRequestStatusTag';
import TQServiceRequestResponseBottpmSheet
  from '../../components/TQServiceRequestResponseBottpmSheet/TQServiceRequestResponseBottpmSheet';
import MakeServiceRequestPublic from './MakeServiceRequestPublic';
import ServiceRequest from './ServiceRequest';
import ServiceRequestChangeLogs from './ServiceRequestChangeLogs';
import ServiceRequestDetailsHeader from './ServiceRequestDetailsHeader';

const ServiceRequestDetails: React.FC = () => {
  const navigate = useAppNavigate();
  const { serviceRequestId } = useParams<{ serviceRequestId: string }>();

  const query = new URLSearchParams(window.location.search);
  const from = query.get('from');

  const { id: userId } = useAppSelector((state) => state.user);

  const { userAccessType } = checkTQConnectAccess();

  const [
    openTQServiceRequestResponseBottpmSheet,
    setOpenTQServiceRequestResponseBottpmSheet,
  ] = useState(false);

  const [isViewingServiceDetails, setIsViewingServiceDetails] = useState(false);
  const [isViewingChat, setIsViewingChat] = useState(false);

  const {
    data: serviceRequestDetails,
    isLoading,
    error,
  } = useTQConnectServiceRequestById(serviceRequestId || '');

  const {
    isMyRequest,
    canOfferHelp,
    canMarkAsCompleted,
    isRequestedToMe,
    isRequestedToMePrivately,
    canMakePublic,
    hasAcceptedResponse,
  } = useMemo(() => {
    const isMyRequest = checkIfCurrentLoggedInUser(
      serviceRequestDetails?.requestedBy?._id || '',
    );
    // if the service request is requested to the current user
    const isRequestedToMe = checkIfCurrentLoggedInUser(
      serviceRequestDetails?.requestedTo?._id || '',
    );
    // if the service request is private and the current user is the requested to
    const isRequestedToMePrivately =
      serviceRequestDetails?.visibility === 'private' &&
      checkIfCurrentLoggedInUser(serviceRequestDetails?.requestedTo?._id || '');
    // if the service request is not created by the current user and it's public, open and not already responded to by the current user
    const canOfferHelp =
      !isMyRequest &&
      serviceRequestDetails?.visibility === 'public' &&
      serviceRequestDetails?.status === 'open' &&
      Boolean(userId && !serviceRequestDetails?.respondents?.includes(userId));
    // if the service request has an accepted response means it's in progress
    const acceptedResponse = serviceRequestDetails?.status === 'in-progress';
    const hasAcceptedResponse = Boolean(acceptedResponse);
    // if the service request is created by the current user and a response is accepted
    const canMarkAsCompleted = isMyRequest && hasAcceptedResponse;
    // show sidebar cases:
    // show resonses list: in public service requests till a response is accepted
    // show messages thread: when a response is accepted or the service request is completed
    const showSidebar =
      serviceRequestDetails?.visibility === 'public' ||
      hasAcceptedResponse ||
      serviceRequestDetails?.status === 'completed';
    // if the service request is created by the current user and it's open/rejected and private and it doesn't have an accepted response, then the user can make it public after 24 hours since it was created
    const wasCreatedMoreThanOneDayAgo = moment(
      serviceRequestDetails?.createdAt,
    ).isBefore(moment(serviceRequestDetails?.createdAt).add(1, 'day'));
    const canMakePublic =
      isMyRequest &&
      !hasAcceptedResponse &&
      serviceRequestDetails?.visibility === 'private' &&
      ['open', 'rejected'].includes(serviceRequestDetails?.status) &&
      wasCreatedMoreThanOneDayAgo;

    return {
      isMyRequest,
      isRequestedToMe,
      isRequestedToMePrivately,
      canOfferHelp,
      canMarkAsCompleted,
      showSidebar,
      hasAcceptedResponse,
      canMakePublic,
    };
  }, [
    serviceRequestDetails?.requestedBy?._id,
    serviceRequestDetails?.status,
    serviceRequestDetails?.visibility,
    serviceRequestDetails?.respondents,
    serviceRequestDetails?.requestedTo?._id,
    serviceRequestDetails?.createdAt,
    userId,
  ]);

  const onGoBack = useCallback(() => {
    navigate(
      !from || from === 'discover'
        ? ROUTES.TQ_CONNECT_SERVICE_REQUESTS_DISCOVER
        : ROUTES.TQ_CONNECT_SERVICE_REQUESTS_MY_ACTIVITY,
      {
        tab: !from || from === 'discover' ? '' : from,
      },
      {
        reset: true,
      },
    );
  }, [from, navigate]);

  const { data: serviceRequestResponsesResult } =
    useTQConnectServiceRequestResponses(
      serviceRequestId || '',
      isRequestedToMePrivately && serviceRequestDetails?.status === 'open',
    );

  const showMessagesThread = useMemo(() => {
    if (serviceRequestDetails?.status === 'in-progress') {
      return (
        serviceRequestDetails?.status === 'in-progress' &&
        !isViewingServiceDetails
      );
    }

    return isViewingChat;
  }, [serviceRequestDetails?.status, isViewingChat, isViewingServiceDetails]);

  const isFooterActionsVisible = useMemo(() => {
    return (
      canOfferHelp ||
      canMarkAsCompleted ||
      (isRequestedToMePrivately && serviceRequestDetails?.status === 'open')
    );
  }, [
    canOfferHelp,
    canMarkAsCompleted,
    isRequestedToMePrivately,
    serviceRequestDetails?.status,
  ]);

  useEffect(() => {
    if (error) {
      showAppError(error);
      onGoBack();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  if (isLoading || !serviceRequestDetails) {
    return (
      <div
        className="service-request-details__page page__container"
        style={{ height: '100%' }}>
        <div
          className="page__content"
          style={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <Loader />
        </div>
      </div>
    );
  }

  return (
    <TQConnectAccessWrapper>
      <div className="service-request-details__page page__container">
        {(isMyRequest || isRequestedToMe) &&
        (hasAcceptedResponse || serviceRequestDetails.status === 'completed') &&
        userAccessType === 'customer' &&
        showMessagesThread ? (
          <div className="page__content">
            <Header
              title={serviceRequestDetails.title}
              handleBack={() => onGoBack()}
              actionItems={[
                <ServiceRequestActions
                  serviceRequestId={serviceRequestDetails._id}
                  serviceRequestDetails={serviceRequestDetails}
                  isMyRequest={isMyRequest}
                  canOfferHelp={canOfferHelp}
                  canMarkAsCompleted={canMarkAsCompleted}
                  cameFrom={from as TQServiceRequestTypeValue}
                  onGoBack={onGoBack}
                  isFooter={false}
                  isRequestedToMePrivately={isRequestedToMePrivately}
                  serviceRequestResponsesResult={
                    serviceRequestResponsesResult as ITQServiceRequestResponsesResult
                  }
                  onViewRequestDetails={() => setIsViewingServiceDetails(true)}
                />,
              ]}
            />
            <ServiceRequestMessageRoom
              serviceRequestId={serviceRequestId || ''}
              status={
                serviceRequestDetails?.status as TQServiceRequestStatusType
              }
              requestedBy={serviceRequestDetails.requestedBy}
              requestedTo={serviceRequestDetails?.requestedTo}
              isFooterActionsVisible={isFooterActionsVisible}
            />
          </div>
        ) : (
          <div className="page__content">
            <Header
              title={''}
              handleBack={() => onGoBack()}
              actionItems={
                userAccessType === 'creator' && !isMyRequest
                  ? []
                  : [
                      <ServiceRequestActions
                        serviceRequestId={serviceRequestDetails._id}
                        serviceRequestDetails={serviceRequestDetails}
                        isMyRequest={isMyRequest}
                        canOfferHelp={canOfferHelp}
                        canMarkAsCompleted={canMarkAsCompleted}
                        cameFrom={from as TQServiceRequestTypeValue}
                        onGoBack={onGoBack}
                        isFooter={false}
                        isRequestedToMePrivately={isRequestedToMePrivately}
                        serviceRequestResponsesResult={
                          serviceRequestResponsesResult as ITQServiceRequestResponsesResult
                        }
                      />,
                    ]
              }
            />
            {serviceRequestDetails?.visibility ? (
              <ServiceRequestStatusTag
                visibility={serviceRequestDetails?.visibility}
                style={{
                  marginTop: 12,
                  marginLeft: 8,
                }}
              />
            ) : null}
            <ServiceRequestDetailsHeader
              serviceRequestDetails={serviceRequestDetails}
              cameFrom={from as TQServiceRequestTypeValue}
              isMyRequest={isMyRequest}
              canOfferHelp={canOfferHelp}
              canMarkAsCompleted={canMarkAsCompleted}
              userAccessType={userAccessType}
              onGoBack={onGoBack}
            />

            <div className="page__content__scroll">
              <ServiceRequest
                serviceRequestDetails={serviceRequestDetails}
                cameFrom={from as TQServiceRequestTypeValue}
                serviceRequestResponsesResult={
                  serviceRequestResponsesResult as ITQServiceRequestResponsesResult
                }
                setOpenTQServiceRequestResponseBottpmSheet={
                  setOpenTQServiceRequestResponseBottpmSheet
                }
              />

              {isMyRequest ||
              isRequestedToMe ||
              userAccessType === 'creator' ? (
                <ServiceRequestChangeLogs
                  serviceRequestDetails={serviceRequestDetails}
                  isMyRequest={isMyRequest}
                  changeLogs={serviceRequestDetails?.changeLogs || []}
                  setOpenTQServiceRequestResponseBottpmSheet={
                    setOpenTQServiceRequestResponseBottpmSheet
                  }
                  onViewChat={() => setIsViewingChat(true)}
                />
              ) : null}

              {canMakePublic ? (
                <MakeServiceRequestPublic
                  serviceRequestId={serviceRequestId || ''}
                  status={serviceRequestDetails?.status}
                />
              ) : null}
            </div>
          </div>
        )}
        <TQServiceRequestResponseBottpmSheet
          open={openTQServiceRequestResponseBottpmSheet}
          onDismiss={() => {
            setOpenTQServiceRequestResponseBottpmSheet(false);
          }}
          serviceRequestId={serviceRequestId || ''}
          isMyRequest={isMyRequest}
        />
        {userAccessType === 'creator' || !isFooterActionsVisible ? null : (
          <div className="service-request-details__actions">
            <ServiceRequestActions
              serviceRequestId={serviceRequestDetails._id}
              serviceRequestDetails={serviceRequestDetails}
              isMyRequest={isMyRequest}
              canOfferHelp={canOfferHelp}
              canMarkAsCompleted={canMarkAsCompleted}
              cameFrom={from as TQServiceRequestTypeValue}
              onGoBack={onGoBack}
              isRequestedToMePrivately={isRequestedToMePrivately}
              isFooter={true}
              serviceRequestResponsesResult={
                serviceRequestResponsesResult as ITQServiceRequestResponsesResult
              }
            />
          </div>
        )}
      </div>
    </TQConnectAccessWrapper>
  );
};

export default memo(ServiceRequestDetails);
